import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { PdfAdBanner } from '../components/AdSystem';
import { 
  Printer, 
  ArrowLeft,
  CheckCircle,
  XCircle,
  AlertTriangle,
  Loader2,
  FileText,
  Target,
  Shield,
  HelpCircle,
  Lightbulb,
  Euro,
  ClipboardList,
  ArrowRight,
  ThumbsUp,
  AlertOctagon,
  Wrench,
  MessageCircle
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export default function PrintableReport() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, getAuthHeaders } = useAuth();
  const [analysis, setAnalysis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    if (id) {
      loadAnalysis(id);
    }
  }, [id]);

  const loadAnalysis = async (analysisId) => {
    setLoading(true);
    try {
      const response = await fetch(`${API_URL}/api/quotes/analyses/${analysisId}`, {
        headers: getAuthHeaders(),
        credentials: 'include'
      });
      if (response.ok) {
        const data = await response.json();
        setAnalysis(data);
      } else {
        setError('Analyse introuvable');
      }
    } catch (err) {
      setError('Erreur lors du chargement');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'long', year: 'numeric'
    });
  };

  const formatDateTime = (date) => {
    return new Date(date).toLocaleDateString('fr-FR', { 
      day: 'numeric', month: 'long', year: 'numeric', hour: '2-digit', minute: '2-digit'
    }).replace(',', ' à');
  };

  const formatCurrency = (amount) => {
    if (!amount && amount !== 0) return 'N/A';
    return new Intl.NumberFormat('fr-FR').format(amount) + ' €';
  };

  const getVerdictInfo = () => {
    const exec = analysis?.executive_summary || {};
    const verdict = exec.verdict?.toLowerCase() || '';
    const score = analysis?.global_score || 0;
    
    if (verdict === 'favorable' || score >= 80) {
      return { label: 'FAVORABLE', color: '#16a34a', bgColor: '#dcfce7', borderColor: '#86efac' };
    }
    if (verdict === 'à négocier' || score >= 60) {
      return { label: 'À NÉGOCIER', color: '#d97706', bgColor: '#fef3c7', borderColor: '#fcd34d' };
    }
    if (verdict === 'attention requise' || score >= 40) {
      return { label: 'ATTENTION REQUISE', color: '#ea580c', bgColor: '#ffedd5', borderColor: '#fdba74' };
    }
    return { label: 'DÉFAVORABLE', color: '#dc2626', bgColor: '#fee2e2', borderColor: '#fca5a5' };
  };

  const getSeverityStyle = (severity) => {
    switch(severity?.toLowerCase()) {
      case 'high':
      case 'élevé':
        return { bg: '#fee2e2', border: '#fca5a5', text: '#dc2626', label: 'ÉLEVÉ' };
      case 'medium':
      case 'moyen':
        return { bg: '#ffedd5', border: '#fdba74', text: '#ea580c', label: 'MOYEN' };
      default:
        return { bg: '#dbeafe', border: '#93c5fd', text: '#2563eb', label: 'FAIBLE' };
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-amber-600" />
      </div>
    );
  }

  if (error || !analysis) {
    return (
      <div className="min-h-screen bg-white flex items-center justify-center">
        <div className="text-center">
          <p className="text-red-600 mb-4">{error || 'Analyse introuvable'}</p>
          <button onClick={() => navigate('/analyze')} className="px-4 py-2 border rounded hover:bg-gray-50">
            Retour
          </button>
        </div>
      </div>
    );
  }

  const verdictInfo = getVerdictInfo();
  const exec = analysis.executive_summary || {};
  const prestations = analysis.prestations_translated || [];
  const risks = analysis.detected_risks || [];
  const questions = analysis.questions_for_artisan || [];
  const missingItems = analysis.potentially_missing_items || [];
  const budget = analysis.budget_estimation || {};
  const reliability = analysis.reliability_score || {};
  const myProject = analysis.if_it_was_my_project || {};
  const tips = analysis.leon_tips || [];
  const insurance = analysis.insurance_check || {};
  const phases = analysis.project_phases || [];
  const positivePoints = analysis.positive_points || [];

  return (
    <>
      {/* Print Styles */}
      <style>{`
        @media print {
          @page { size: A4; margin: 12mm; }
          body { -webkit-print-color-adjust: exact !important; print-color-adjust: exact !important; }
          .no-print { display: none !important; }
          .print-cover { page-break-after: always; }
          .avoid-break { page-break-inside: avoid; break-inside: avoid; }
          .section-title { margin-top: 8mm; }
        }
      `}</style>

      <div className="min-h-screen bg-gray-100 print:bg-white">
        {/* Controls - Hidden on print */}
        <div className="no-print sticky top-0 z-50 bg-white border-b shadow-sm">
          <div className="max-w-4xl mx-auto px-6 py-4">
            <div className="flex items-center justify-between">
              <button onClick={() => navigate(-1)} className="flex items-center gap-2 text-gray-600 hover:text-gray-900">
                <ArrowLeft className="w-4 h-4" />
                Retour
              </button>
              <button onClick={() => window.print()} className="flex items-center gap-2 px-6 py-3 bg-amber-600 hover:bg-amber-700 text-white font-semibold rounded-lg shadow">
                <Printer className="w-5 h-5" />
                Imprimer / Enregistrer en PDF
              </button>
            </div>
            <div className="mt-3 p-3 bg-amber-50 border border-amber-200 rounded-lg">
              <p className="text-sm text-amber-800 text-center">
                <strong>Astuce :</strong> Utilisez <kbd className="px-2 py-0.5 bg-amber-100 rounded text-xs font-mono">Ctrl + P</kbd> (ou <kbd className="px-2 py-0.5 bg-amber-100 rounded text-xs font-mono">Cmd + P</kbd> sur Mac) puis sélectionnez <strong>"Enregistrer au format PDF"</strong>
              </p>
            </div>
          </div>
        </div>

        {/* Document Container */}
        <div className="max-w-[210mm] mx-auto bg-white shadow-xl print:shadow-none print:max-w-none">
          
          {/* === PAGE DE COUVERTURE === */}
          <div className="print-cover" style={{ padding: '15mm', minHeight: '270mm', display: 'flex', flexDirection: 'column' }}>
            <div className="flex items-center justify-between pb-4 border-b-4 border-amber-500">
              <div className="flex items-center gap-4">
                <img src="/logo-phenix.png" alt="Assistant by Léon" className="h-14 w-auto" onError={(e) => e.target.style.display='none'} />
                <span className="font-bold text-xl text-gray-800">ASSISTANT BY LÉON</span>
              </div>
              <div className="text-right text-sm text-gray-500">
                <p className="font-semibold text-gray-700">Rapport d'analyse expert</p>
                <p>{formatDate(new Date())}</p>
              </div>
            </div>

            <div className="flex-1 flex flex-col items-center justify-center text-center py-8">
              <h1 className="text-3xl font-bold text-gray-900 tracking-tight mb-2">RAPPORT D'ANALYSE EXPERT</h1>
              <p className="text-lg text-amber-600 font-medium mb-10">Analyse complète de votre devis travaux</p>
              
              {/* Score Box */}
              <div className="w-72 p-6 rounded-2xl mb-8" style={{ backgroundColor: verdictInfo.bgColor, border: `3px solid ${verdictInfo.borderColor}` }}>
                <p className="text-xs text-gray-600 uppercase tracking-widest mb-2">Verdict de Léon</p>
                <p className="text-lg font-bold mb-3" style={{ color: verdictInfo.color }}>{verdictInfo.label}</p>
                <p className="text-6xl font-bold" style={{ color: verdictInfo.color }}>
                  {analysis.global_score}<span className="text-2xl text-gray-400 font-normal">/100</span>
                </p>
              </div>

              <div className="space-y-1 text-gray-600 text-sm">
                {analysis.filename && <p><span className="font-semibold">Document :</span> {analysis.filename}</p>}
                <p><span className="font-semibold">Date :</span> {formatDateTime(analysis.created_at || new Date())}</p>
                {user?.name && <p><span className="font-semibold">Pour :</span> {user.name}</p>}
              </div>
            </div>

            <div className="pt-4 border-t border-gray-200 text-center">
              <p className="text-xs text-gray-400">Généré par Assistant by Léon - L'IA qui sécurise vos travaux</p>
            </div>
          </div>

          {/* === CONTENU PRINCIPAL === */}
          <div style={{ padding: '12mm' }}>
            
            {/* Header compact */}
            <div className="flex items-center justify-between pb-2 mb-4 border-b-2 border-amber-500">
              <div className="flex items-center gap-2">
                <span className="font-bold text-gray-800 text-sm">ASSISTANT BY LÉON</span>
              </div>
              <span className="text-xs text-gray-500">Rapport d'analyse expert</span>
            </div>

            {/* === SECTION 1: RÉSUMÉ EXÉCUTIF === */}
            <section className="mb-6 avoid-break">
              <div className="flex items-center gap-2 mb-3 section-title">
                <div className="w-7 h-7 rounded bg-amber-500 flex items-center justify-center">
                  <FileText className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-bold text-gray-900">1. Résumé de l'analyse</h2>
              </div>
              
              <div className="p-4 rounded-lg mb-3" style={{ backgroundColor: verdictInfo.bgColor, border: `2px solid ${verdictInfo.borderColor}` }}>
                <div className="flex items-center justify-between mb-2">
                  <span className="font-bold text-lg" style={{ color: verdictInfo.color }}>{verdictInfo.label}</span>
                  <span className="text-2xl font-bold" style={{ color: verdictInfo.color }}>{analysis.global_score}/100</span>
                </div>
                {exec.main_observation && <p className="text-sm text-gray-700">{exec.main_observation}</p>}
              </div>

              {exec.key_points?.length > 0 && (
                <div className="bg-gray-50 p-3 rounded-lg">
                  <p className="font-semibold text-sm text-gray-700 mb-2">Points clés à retenir :</p>
                  {exec.key_points.map((point, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1">
                      <ArrowRight className="w-3 h-3 text-amber-500 mt-1 flex-shrink-0" />
                      <span className="text-xs text-gray-600">{point}</span>
                    </div>
                  ))}
                </div>
              )}

              {exec.recommended_action && (
                <div className="mt-3 p-3 bg-amber-50 border-l-4 border-amber-500 rounded-r-lg">
                  <p className="text-xs font-semibold text-amber-800">Action recommandée :</p>
                  <p className="text-sm text-gray-700">{exec.recommended_action}</p>
                </div>
              )}
            </section>

            {/* === SECTION 2: COMPRENDRE LES PRESTATIONS === */}
            {prestations.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-amber-500 flex items-center justify-center">
                    <ClipboardList className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">2. Comprendre les prestations ({prestations.length})</h2>
                </div>
                
                <div className="space-y-3">
                  {prestations.map((p, i) => {
                    const isZeroPrice = p.price_ht === 0 || p.price_ht === "0" || (!p.price_ht && !p.price_ttc);
                    return (
                      <div key={i} className={`p-3 rounded-lg border avoid-break ${isZeroPrice ? 'bg-amber-50 border-amber-200' : 'bg-gray-50 border-gray-200'}`}>
                        <div className="flex justify-between items-start mb-1">
                          <div className="flex-1">
                            {p.reference && p.reference !== "X" && (
                              <span className="text-xs text-amber-600 font-mono">Réf: {p.reference}</span>
                            )}
                            <p className="text-xs text-gray-500 font-mono">{p.original}</p>
                          </div>
                          <span className={`font-bold text-sm ${isZeroPrice ? 'text-amber-600' : 'text-gray-800'}`}>
                            {isZeroPrice ? '0 €' : (p.price_ttc ? formatCurrency(p.price_ttc) : (p.price_ht ? formatCurrency(p.price_ht) + ' HT' : '-'))}
                          </span>
                        </div>
                        <div className="flex items-center gap-1 mb-1">
                          <ArrowRight className="w-3 h-3 text-amber-500" />
                          <span className="text-xs text-amber-600">En langage simple :</span>
                        </div>
                        <p className="text-sm text-gray-800 mb-1">{p.simplified}</p>
                        {p.leon_comment && (
                          <div className="flex items-start gap-1 mt-2 p-2 bg-amber-100 rounded">
                            <Lightbulb className="w-3 h-3 text-amber-600 mt-0.5 flex-shrink-0" />
                            <span className="text-xs text-amber-800">{p.leon_comment}</span>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === SECTION 3: POINTS POSITIFS === */}
            {positivePoints.length > 0 && (
              <section className="mb-6 avoid-break">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-green-500 flex items-center justify-center">
                    <ThumbsUp className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">3. Points positifs du devis</h2>
                </div>
                <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                  {positivePoints.map((point, i) => (
                    <div key={i} className="flex items-start gap-2 mb-1">
                      <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                      <span className="text-sm text-gray-700">{point}</span>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === SECTION 4: RISQUES DÉTECTÉS === */}
            {risks.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-red-500 flex items-center justify-center">
                    <AlertOctagon className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">4. Risques détectés ({risks.length})</h2>
                </div>
                <div className="space-y-3">
                  {risks.map((risk, i) => {
                    const style = getSeverityStyle(risk.severity);
                    return (
                      <div key={i} className="p-3 rounded-lg border avoid-break" style={{ backgroundColor: style.bg, borderColor: style.border }}>
                        <div className="flex items-center gap-2 mb-1">
                          <AlertTriangle className="w-4 h-4" style={{ color: style.text }} />
                          <span className="font-bold text-sm" style={{ color: style.text }}>{risk.risk_title}</span>
                          <span className="px-2 py-0.5 rounded text-xs font-bold text-white" style={{ backgroundColor: style.text }}>{style.label}</span>
                        </div>
                        {risk.risk_description && <p className="text-xs text-gray-700 mb-1">{risk.risk_description}</p>}
                        {risk.consequence && <p className="text-xs text-gray-600 italic">Conséquence : {risk.consequence}</p>}
                        {risk.recommendation && (
                          <div className="mt-2 p-2 bg-white rounded border border-gray-200">
                            <p className="text-xs text-gray-700"><strong>Conseil :</strong> {risk.recommendation}</p>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === SECTION 5: QUESTIONS À POSER === */}
            {questions.length > 0 && (
              <section className="mb-6 avoid-break">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-blue-500 flex items-center justify-center">
                    <HelpCircle className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">5. Questions à poser à l'artisan ({questions.length})</h2>
                </div>
                <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 space-y-2">
                  {questions.map((q, i) => {
                    const question = typeof q === 'string' ? q : q.question;
                    const why = typeof q === 'object' ? q.why_important : null;
                    return (
                      <div key={i} className="pb-2 border-b border-blue-100 last:border-0 last:pb-0">
                        <p className="text-sm font-medium text-gray-800">{i+1}. {question}</p>
                        {why && <p className="text-xs text-gray-500 italic mt-1">Pourquoi : {why}</p>}
                      </div>
                    );
                  })}
                </div>
              </section>
            )}

            {/* === SECTION 6: POSTES POTENTIELLEMENT OUBLIÉS === */}
            {missingItems.length > 0 && (
              <section className="mb-6 avoid-break">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-orange-500 flex items-center justify-center">
                    <AlertTriangle className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">6. Postes potentiellement oubliés ({missingItems.length})</h2>
                </div>
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-3 space-y-3">
                  {missingItems.map((item, i) => (
                    <div key={i} className="pb-2 border-b border-orange-100 last:border-0 last:pb-0">
                      <p className="font-semibold text-sm text-gray-800">{item.item}</p>
                      {item.why_usually_needed && <p className="text-xs text-gray-600">{item.why_usually_needed}</p>}
                      {item.estimated_cost && <p className="text-xs text-orange-700">Coût estimé : {item.estimated_cost}</p>}
                      {item.question_to_ask && (
                        <p className="text-xs text-gray-700 mt-1"><strong>À demander :</strong> {item.question_to_ask}</p>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === SECTION 7: ESTIMATION DU BUDGET === */}
            <section className="mb-6 avoid-break">
              <div className="flex items-center gap-2 mb-3 section-title">
                <div className="w-7 h-7 rounded bg-emerald-500 flex items-center justify-center">
                  <Euro className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-bold text-gray-900">7. Estimation du budget réel</h2>
              </div>
              <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                <table className="w-full text-sm">
                  <tbody>
                    <tr className="border-b border-gray-200">
                      <td className="p-2 text-gray-600">Total du devis HT</td>
                      <td className="p-2 text-right font-semibold">{formatCurrency(budget.quote_total_ht || analysis.price_analysis?.total_ht)}</td>
                    </tr>
                    <tr className="border-b border-gray-200">
                      <td className="p-2 text-gray-600">Total du devis TTC</td>
                      <td className="p-2 text-right font-semibold">{formatCurrency(budget.quote_total_ttc || analysis.price_analysis?.total_ttc)}</td>
                    </tr>
                    <tr className="bg-amber-50">
                      <td className="p-2 font-semibold text-gray-800">Budget final estimé</td>
                      <td className="p-2 text-right font-bold text-amber-700">
                        {budget.estimated_final_budget_low && budget.estimated_final_budget_high 
                          ? `${formatCurrency(budget.estimated_final_budget_low)} - ${formatCurrency(budget.estimated_final_budget_high)}`
                          : 'N/A'}
                      </td>
                    </tr>
                  </tbody>
                </table>
              </div>
              {budget.additional_costs_breakdown?.length > 0 && (
                <div className="mt-2 p-2 bg-gray-50 rounded text-xs">
                  <p className="font-semibold text-gray-700 mb-1">Coûts supplémentaires possibles :</p>
                  {budget.additional_costs_breakdown.map((cost, i) => (
                    <p key={i} className="text-gray-600">• {cost.item} : ~{cost.estimated_cost}€ {cost.reason && `(${cost.reason})`}</p>
                  ))}
                </div>
              )}
              {budget.leon_budget_advice && (
                <div className="mt-2 p-2 bg-amber-50 border-l-3 border-amber-500 rounded-r">
                  <p className="text-xs text-amber-800"><strong>Conseil de Léon :</strong> {budget.leon_budget_advice}</p>
                </div>
              )}
            </section>

            {/* === SECTION 8: SCORE DE FIABILITÉ === */}
            <section className="mb-6 avoid-break">
              <div className="flex items-center gap-2 mb-3 section-title">
                <div className="w-7 h-7 rounded bg-purple-500 flex items-center justify-center">
                  <Target className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-bold text-gray-900">8. Score de fiabilité détaillé</h2>
              </div>
              <div className="text-center mb-3">
                <span className="text-4xl font-bold" style={{ color: verdictInfo.color }}>{analysis.global_score}</span>
                <span className="text-xl text-gray-400">/100</span>
              </div>
              {(reliability.administrative_score || reliability.technical_score || reliability.financial_score) && (
                <div className="bg-gray-50 border border-gray-200 rounded-lg overflow-hidden">
                  <table className="w-full text-xs">
                    <thead className="bg-amber-500 text-white">
                      <tr>
                        <th className="p-2 text-left">Catégorie</th>
                        <th className="p-2 text-center">Score</th>
                        <th className="p-2 text-center">Poids</th>
                      </tr>
                    </thead>
                    <tbody>
                      <tr className="border-b border-gray-200">
                        <td className="p-2">Administratif</td>
                        <td className="p-2 text-center font-semibold">{reliability.administrative_score?.score || '-'}/{reliability.administrative_score?.max || 30}</td>
                        <td className="p-2 text-center">30%</td>
                      </tr>
                      <tr className="border-b border-gray-200">
                        <td className="p-2">Technique</td>
                        <td className="p-2 text-center font-semibold">{reliability.technical_score?.score || '-'}/{reliability.technical_score?.max || 40}</td>
                        <td className="p-2 text-center">40%</td>
                      </tr>
                      <tr>
                        <td className="p-2">Financier</td>
                        <td className="p-2 text-center font-semibold">{reliability.financial_score?.score || '-'}/{reliability.financial_score?.max || 30}</td>
                        <td className="p-2 text-center">30%</td>
                      </tr>
                    </tbody>
                  </table>
                </div>
              )}
            </section>

            {/* === SECTION 9: SI C'ÉTAIT MON CHANTIER === */}
            {(myProject.intro || myProject.checkpoints?.length > 0) && (
              <section className="mb-6 avoid-break">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-indigo-500 flex items-center justify-center">
                    <Wrench className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">9. Si c'était mon chantier</h2>
                </div>
                {myProject.intro && (
                  <p className="text-sm text-gray-600 italic mb-3">{myProject.intro}</p>
                )}
                {myProject.checkpoints?.length > 0 && (
                  <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-3 space-y-2">
                    {myProject.checkpoints.map((cp, i) => {
                      const checkpoint = typeof cp === 'string' ? cp : cp.checkpoint;
                      const why = typeof cp === 'object' ? cp.why : null;
                      return (
                        <div key={i}>
                          <p className="text-sm font-medium text-gray-800">{i+1}. {checkpoint}</p>
                          {why && <p className="text-xs text-gray-500 ml-4">Pourquoi : {why}</p>}
                        </div>
                      );
                    })}
                  </div>
                )}
                {myProject.final_advice && (
                  <div className="mt-3 p-3 bg-indigo-100 rounded-lg">
                    <p className="text-sm text-indigo-800"><strong>Mon conseil final :</strong> {myProject.final_advice}</p>
                  </div>
                )}
              </section>
            )}

            {/* === SECTION 10: ASTUCES DE LÉON === */}
            {tips.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-amber-500 flex items-center justify-center">
                    <Lightbulb className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">10. Astuces de Léon</h2>
                </div>
                <div className="space-y-3">
                  {tips.map((tip, i) => (
                    <div key={i} className="p-3 bg-amber-50 border border-amber-200 rounded-lg avoid-break">
                      <p className="font-semibold text-sm text-amber-800 mb-1">{tip.tip_title}</p>
                      <p className="text-xs text-gray-700">{tip.tip_content}</p>
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === SECTION 11: VÉRIFICATION ASSURANCE === */}
            <section className="mb-6 avoid-break">
              <div className="flex items-center gap-2 mb-3 section-title">
                <div className="w-7 h-7 rounded bg-red-500 flex items-center justify-center">
                  <Shield className="w-4 h-4 text-white" />
                </div>
                <h2 className="text-base font-bold text-gray-900">11. Vérification de l'assurance</h2>
              </div>
              
              {!insurance.insurance_mentioned ? (
                <div className="p-3 bg-red-50 border-2 border-red-300 rounded-lg mb-3">
                  <div className="flex items-center gap-2">
                    <XCircle className="w-5 h-5 text-red-600" />
                    <span className="font-bold text-red-700">ATTENTION : L'assurance décennale n'est PAS mentionnée !</span>
                  </div>
                </div>
              ) : (
                <div className="p-3 bg-green-50 border border-green-200 rounded-lg mb-3">
                  <div className="flex items-center gap-2">
                    <CheckCircle className="w-5 h-5 text-green-600" />
                    <span className="font-medium text-green-700">Assurance mentionnée</span>
                  </div>
                  {insurance.insurance_details && <p className="text-xs text-gray-600 mt-1">{insurance.insurance_details}</p>}
                </div>
              )}
              
              <div className="bg-gray-50 p-3 rounded-lg text-xs text-gray-700 space-y-2">
                <p><strong>Important :</strong> Avant de signer, demandez l'attestation d'assurance professionnelle et vérifiez que l'activité déclarée correspond aux travaux du devis.</p>
                {insurance.leon_warning && <p className="text-amber-700">{insurance.leon_warning}</p>}
                {insurance.action_required && <p><strong>Action :</strong> {insurance.action_required}</p>}
              </div>
            </section>

            {/* === SECTION 12: ÉTAPES DU CHANTIER === */}
            {phases.length > 0 && (
              <section className="mb-6">
                <div className="flex items-center gap-2 mb-3 section-title">
                  <div className="w-7 h-7 rounded bg-teal-500 flex items-center justify-center">
                    <ClipboardList className="w-4 h-4 text-white" />
                  </div>
                  <h2 className="text-base font-bold text-gray-900">12. Étapes probables du chantier</h2>
                </div>
                <div className="space-y-2">
                  {phases.map((phase, i) => (
                    <div key={i} className="p-3 bg-teal-50 border border-teal-200 rounded-lg avoid-break">
                      <div className="flex items-center gap-2 mb-1">
                        <span className="w-6 h-6 rounded-full bg-teal-500 text-white text-xs font-bold flex items-center justify-center">
                          {phase.phase_number || i+1}
                        </span>
                        <span className="font-semibold text-sm text-gray-800">{phase.phase_title}</span>
                        {phase.estimated_duration && <span className="text-xs text-gray-500">({phase.estimated_duration})</span>}
                      </div>
                      {phase.phase_description && <p className="text-xs text-gray-600 ml-8">{phase.phase_description}</p>}
                      {phase.key_points?.length > 0 && (
                        <div className="ml-8 mt-1">
                          {phase.key_points.map((kp, j) => (
                            <p key={j} className="text-xs text-gray-500">→ {kp}</p>
                          ))}
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </section>
            )}

            {/* === AVERTISSEMENT LÉGAL === */}
            <section className="mt-8 pt-4 border-t-2 border-amber-500">
              <h3 className="text-sm font-bold text-gray-900 mb-2">Avertissement et limitations</h3>
              <div className="bg-gray-50 border border-gray-200 rounded-lg p-3 text-xs text-gray-600 space-y-2">
                <p><strong>Ce rapport est fourni à titre informatif uniquement.</strong></p>
                <p>Assistant by Léon fournit une analyse automatisée d'aide à la décision. Les résultats ne constituent pas un diagnostic technique exhaustif ni un avis juridique. L'utilisateur reste seul responsable de ses décisions.</p>
                <p>Avant toute signature, il est recommandé de faire procéder à toute vérification complémentaire utile auprès de professionnels qualifiés et assurés.</p>
              </div>
              <div className="mt-4 text-center">
                <p className="text-xs text-gray-400">© {new Date().getFullYear()} Assistant by Léon - Un produit de l'écosystème Phénix</p>
                <p className="text-xs text-gray-400">Rapport généré le {formatDateTime(new Date())}</p>
              </div>
            </section>

            {/* === PUBLICITÉ (Utilisateurs gratuits uniquement) === */}
            <PdfAdBanner />

          </div>
        </div>
      </div>
    </>
  );
}
